import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Logo from '../components/Logo';
import DISCChart from '../components/DISCChart';
import DISCCircle from '../components/DISCCircle';
import { getAssessmentResults } from '../utils/api';
import { getContentForStyle } from '../utils/personalityContent';
import { Printer, CheckCircle, AlertTriangle, FileText } from 'lucide-react';

const Results = ({ view = 'full' }) => {
  const { uuid } = useParams();
  const location = useLocation();
  const [results, setResults] = useState(location.state?.results || null);
  const [assessmentData, setAssessmentData] = useState(null);
  const [loading, setLoading] = useState(!location.state?.results);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!results) {
      loadResults();
    }
  }, [uuid]);

  const loadResults = async () => {
    try {
      const response = await getAssessmentResults(uuid);
      setAssessmentData(response.data);
      setResults({
        environmentalStyle: {
          D: response.data.env_d_score,
          I: response.data.env_i_score,
          S: response.data.env_s_score,
          C: response.data.env_c_score,
        },
        basicStyle: {
          D: response.data.basic_d_score,
          I: response.data.basic_i_score,
          S: response.data.basic_s_score,
          C: response.data.basic_c_score,
        },
        personalityStyle: {
          primary: response.data.primary_style,
          secondary: response.data.secondary_style,
          blend: response.data.style_blend,
        },
        descriptions: response.data.descriptions,
        descriptiveWords: response.data.descriptiveWords,
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load results');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tsvmap-orange" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Results</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const { environmentalStyle, basicStyle, personalityStyle, descriptions, descriptiveWords } = results || {};

  const styleColors = {
    D: 'bg-red-600',
    I: 'bg-tsvmap-orange',
    S: 'bg-blue-600',
    C: 'bg-yellow-500',
  };

  const styleNames = {
    D: 'Dominant',
    I: 'Inspiring',
    S: 'Supportive',
    C: 'Cautious',
    LEVEL: 'Level (Balanced)',
  };

  // Get comprehensive content for the personality type
  const content = getContentForStyle(personalityStyle?.primary, personalityStyle?.blend);
  const isLevel = personalityStyle?.blend === 'LEVEL';

  // Concise One View - Just the graphs
  if (view === 'conciseone') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Logo size="small" />
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 text-white border border-white/30 rounded-lg hover:bg-white/10 no-print"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-tsvmap-blue to-tsvmap-blue-dark p-6 text-white">
              <h1 className="text-2xl font-bold">DISC Profile - Graph View</h1>
              {assessmentData && (
                <p className="text-blue-100">
                  {assessmentData.respondent_first_name} {assessmentData.respondent_last_name}
                </p>
              )}
            </div>

            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <DISCChart
                    scores={environmentalStyle}
                    title="Graph I - Environmental Style"
                    size="large"
                  />
                  <p className="text-sm text-gray-500 text-center mt-4">
                    How others tend to see you (Most - Least)
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <DISCChart
                    scores={basicStyle}
                    title="Graph II - Basic Style"
                    size="large"
                  />
                  <p className="text-sm text-gray-500 text-center mt-4">
                    Who you are inside (Most selections)
                  </p>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-lg">
                  <span className="font-semibold">Primary Style:</span>{' '}
                  <span className="inline-flex items-center gap-2">
                    <span className={`w-4 h-4 rounded-full ${styleColors[personalityStyle?.primary]}`}></span>
                    {styleNames[personalityStyle?.primary]} ({personalityStyle?.primary})
                  </span>
                </p>
                {personalityStyle?.blend && (
                  <p className="text-gray-600 mt-1">Style Blend: {personalityStyle.blend}</p>
                )}
              </div>
            </div>
          </div>
        </main>

        <footer className="py-6 text-center text-sm text-gray-500 no-print">
          <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // Concise Two View - Primary style and descriptive words
  if (view === 'concisetwo') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Logo size="small" />
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 text-white border border-white/30 rounded-lg hover:bg-white/10 no-print"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-tsvmap-blue to-tsvmap-blue-dark p-6 text-white">
              <h1 className="text-2xl font-bold">Personality Profile Summary</h1>
              {assessmentData && (
                <p className="text-blue-100">
                  {assessmentData.respondent_first_name} {assessmentData.respondent_last_name}
                </p>
              )}
            </div>

            <div className="p-8">
              <div className="flex flex-col md:flex-row items-center gap-8 mb-8">
                <div className="flex-shrink-0">
                  <DISCCircle
                    primaryStyle={personalityStyle?.primary}
                    blend={personalityStyle?.blend}
                    size={180}
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <span className={`w-4 h-4 rounded-full ${styleColors[personalityStyle?.primary]}`} />
                    <span className="text-sm text-gray-500">Your Primary Style</span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-1">
                    {descriptions?.name || styleNames[personalityStyle?.primary]} ({personalityStyle?.primary})
                  </h2>
                  <p className="text-lg text-gray-600">{descriptions?.subtitle}</p>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Words that Best Describe You
                </h3>
                <div className="flex flex-wrap gap-2">
                  {descriptiveWords?.slice(0, 20).map((word) => (
                    <span
                      key={word}
                      className="px-3 py-1 bg-tsvmap-orange/10 text-tsvmap-orange rounded-full text-sm font-medium"
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Characteristics</h3>
                <div className="flex flex-wrap gap-2">
                  {descriptions?.characteristics?.map((char) => (
                    <span
                      key={char}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-6 text-center text-sm text-gray-500 no-print">
          <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // Concise View - Graphs + Primary style + Key info
  if (view === 'concise') {
    return (
      <div className="min-h-screen bg-gray-50">
        <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Logo size="small" />
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 text-white border border-white/30 rounded-lg hover:bg-white/10 no-print"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-tsvmap-blue to-tsvmap-blue-dark p-6 text-white">
              <h1 className="text-2xl font-bold">TSVMap DISC Profile</h1>
              {assessmentData && (
                <p className="text-blue-100">
                  {assessmentData.respondent_first_name} {assessmentData.respondent_last_name}
                </p>
              )}
            </div>

            {/* Primary Style */}
            <div className="p-6 border-b">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-shrink-0">
                  <DISCCircle
                    primaryStyle={personalityStyle?.primary}
                    blend={personalityStyle?.blend}
                    size={150}
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 mb-1">
                    <span className={`w-3 h-3 rounded-full ${styleColors[personalityStyle?.primary]}`} />
                    <span className="text-xs text-gray-500">Primary Style</span>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {descriptions?.name || styleNames[personalityStyle?.primary]} ({personalityStyle?.primary})
                  </h2>
                  <p className="text-gray-600">{descriptions?.subtitle}</p>
                </div>
              </div>
            </div>

            {/* Graphs */}
            <div className="p-6 border-b">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <DISCChart
                    scores={environmentalStyle}
                    title="Environmental Style"
                    size="medium"
                  />
                  <p className="text-xs text-gray-500 text-center mt-2">
                    How others see you
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <DISCChart
                    scores={basicStyle}
                    title="Basic Style"
                    size="medium"
                  />
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Who you are inside
                  </p>
                </div>
              </div>
            </div>

            {/* Descriptive Words */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-gray-900 mb-3">Words that Describe You</h3>
              <div className="flex flex-wrap gap-2">
                {descriptiveWords?.slice(0, 12).map((word) => (
                  <span
                    key={word}
                    className="px-3 py-1 bg-tsvmap-orange/10 text-tsvmap-orange rounded-full text-sm font-medium"
                  >
                    {word}
                  </span>
                ))}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{basicStyle?.D || 0}</div>
                  <div className="text-xs text-gray-500">Dominant</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-tsvmap-orange">{basicStyle?.I || 0}</div>
                  <div className="text-xs text-gray-500">Inspiring</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{basicStyle?.S || 0}</div>
                  <div className="text-xs text-gray-500">Supportive</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-500">{basicStyle?.C || 0}</div>
                  <div className="text-xs text-gray-500">Cautious</div>
                </div>
              </div>
            </div>
          </div>
        </main>

        <footer className="py-6 text-center text-sm text-gray-500 no-print">
          <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
        </footer>
      </div>
    );
  }

  // Full View (default)
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header style={{ backgroundColor: '#0B2134' }} className="shadow-sm no-print">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size="small" />
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="flex items-center px-4 py-2 text-white border border-white/30 rounded-lg hover:bg-white/10"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Success Banner */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8 flex items-center justify-between no-print">
          <div className="flex items-center">
            <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
            <div>
              <h3 className="font-semibold text-green-800">Assessment Complete!</h3>
              <p className="text-green-700 text-sm">
                Your results have been saved and sent to the administrator.
              </p>
            </div>
          </div>
          <Link
            to={`/report/${uuid}`}
            className="flex items-center px-4 py-2 bg-tsvmap-orange text-white rounded-lg hover:bg-tsvmap-orange-dark transition-colors"
          >
            <FileText className="h-4 w-4 mr-2" />
            Full Report
          </Link>
        </div>

        {/* Main Results Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
          {/* Header */}
          <div className="bg-gradient-to-r from-tsvmap-blue to-tsvmap-blue-dark p-6 text-white">
            <h1 className="text-2xl font-bold mb-1">TSVMap Personality Report</h1>
            {assessmentData && (
              <p className="text-blue-100">
                {assessmentData.respondent_first_name} {assessmentData.respondent_last_name}
              </p>
            )}
          </div>

          {/* Primary Style */}
          <div className="p-6 border-b">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-shrink-0">
                <DISCCircle
                  primaryStyle={personalityStyle?.primary}
                  blend={personalityStyle?.blend}
                  size={200}
                />
              </div>
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 mb-2">
                  <span
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: isLevel ? '#6B7280' : (styleColors[personalityStyle?.primary]?.replace('bg-', '') || '#666') }}
                  />
                  <span className="text-sm text-gray-500">
                    {isLevel ? 'Your Personality Type' : 'Your Primary Style'}
                  </span>
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-1">
                  {isLevel ? 'Level (Balanced)' : `${content?.name || styleNames[personalityStyle?.primary]} (${personalityStyle?.primary})`}
                </h2>
                <p className="text-lg text-gray-600 mb-4">{content?.subtitle || descriptions?.subtitle}</p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {(content?.characteristics || descriptions?.characteristics)?.map((char) => (
                    <span
                      key={char}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {char}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Style Graphs */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">Your DISC Profile</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <DISCChart
                  scores={environmentalStyle}
                  title="Environmental Style"
                  size="medium"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  How others tend to see you
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <DISCChart
                  scores={basicStyle}
                  title="Basic Style"
                  size="medium"
                />
                <p className="text-xs text-gray-500 text-center mt-2">
                  Who you are inside
                </p>
              </div>
            </div>
          </div>

          {/* Descriptive Words */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Words that Best Describe You
            </h3>
            <div className="flex flex-wrap gap-2">
              {descriptiveWords?.slice(0, 15).map((word) => (
                <span
                  key={word}
                  className="px-3 py-1 bg-tsvmap-orange/10 text-tsvmap-orange rounded-full text-sm font-medium"
                >
                  {word}
                </span>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">
              These words may describe you more or less depending on the situation.
            </p>
          </div>

          {/* Strengths */}
          <div className="p-6 border-b print-break">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Strengths</h3>
            <ul className="space-y-3">
              {descriptions?.strengths?.map((strength, idx) => (
                <li key={idx} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{strength}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Keys to Excellence */}
          <div className="p-6 border-b">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Keys to Excellence</h3>
            <p className="text-gray-600 mb-4">
              These keys are important in adapting to the needs and perspective of others.
            </p>
            <ul className="space-y-3">
              {descriptions?.keysToExcellence?.map((key, idx) => (
                <li key={idx} className="flex items-start">
                  <span className="w-6 h-6 bg-tsvmap-orange text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-gray-700">{key}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Team Info */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">As a Team Member</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Main Motivation</h4>
                <p className="text-gray-600">{descriptions?.mainMotivation}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Ideal Environment</h4>
                <p className="text-gray-600">{descriptions?.idealEnvironment}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Keys to Motivate</h4>
                <p className="text-gray-600">{descriptions?.keysToMotivate}</p>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Possible Blind Spots</h4>
                <p className="text-gray-600">{descriptions?.possibleBlindSpots}</p>
              </div>
            </div>
          </div>
        </div>

        {/* DISC Model Explanation */}
        <div className="bg-white rounded-lg shadow-lg p-6 print-break">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            The "DISC" Model of Human Behavior
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                D
              </div>
              <h4 className="font-semibold text-red-900">Dominant</h4>
              <p className="text-red-700 text-xs">Outgoing & Task-Oriented</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
              <div className="w-10 h-10 bg-tsvmap-orange text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                I
              </div>
              <h4 className="font-semibold text-orange-900">Inspiring</h4>
              <p className="text-orange-700 text-xs">Outgoing & People-Oriented</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                S
              </div>
              <h4 className="font-semibold text-blue-900">Supportive</h4>
              <p className="text-blue-700 text-xs">Reserved & People-Oriented</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
              <div className="w-10 h-10 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-2">
                C
              </div>
              <h4 className="font-semibold text-yellow-900">Cautious</h4>
              <p className="text-yellow-700 text-xs">Reserved & Task-Oriented</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-500 no-print">
        <p>© {new Date().getFullYear()} TSVMap. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Results;
